<template>
  <div class="stock-match-score" :class="{ 'stock-match-score--full': !reference && isFullMatch }">
    <template v-if="reference">
      <p class="stock-match-score__label">{{ i18n.t('/stockmatch/referencebasislabel') }}</p>
      <p class="stock-match-score__value">
        {{ i18n.t('/stockmatch/referenceoptioncount', { count: rows.length }) }}
      </p>
      <p class="stock-match-score__muted">{{ i18n.t('/stockmatch/referencebasistext') }}</p>
    </template>
    <template v-else>
      <p class="stock-match-score__label">{{ matchTypeLabel }}</p>
      <div class="stock-match-score__line">
        <p class="stock-match-score__value">
          {{ i18n.t('/stockmatch/matchscore', { matched: matchedCount, total: rows.length }) }}
        </p>
        <span v-if="extras.length" class="stock-match-score__extras-badge">
          <icon icon="plus" :width="10" :height="10" />
          {{ i18n.t('/stockmatch/extrascount', { count: extras.length }) }}
        </span>
      </div>
      <div class="stock-match-score__segments" role="img" :aria-label="scoreSentence">
        <span
          v-for="(row, index) in rows"
          :key="row.code + index"
          :class="{ 'stock-match-score__segment--matched': index < matchedCount }"
        ></span>
      </div>
      <p class="stock-match-score__muted">{{ scoreSentence }}</p>
    </template>
  </div>
</template>

<script>
import Icon from '@/components/generic/presentational/atoms/icon/Icon.vue'

export default {
  components: { Icon },
  props: {
    reference: {
      type: Boolean,
      default: false,
    },
    rows: {
      type: Array,
      default: () => [],
    },
    extras: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
    }
  },
  computed: {
    matchedCount() {
      return this.rows.filter((row) => row.matches).length
    },
    isFullMatch() {
      return this.matchedCount === this.rows.length
    },
    matchTypeLabel() {
      return this.i18n.t(
        this.isFullMatch && !this.extras.length
          ? '/stockmatch/exactmatch'
          : '/stockmatch/similarmatch'
      )
    },
    scoreSentence() {
      if (this.isFullMatch) return this.i18n.t('/stockmatch/fullmatchtext')
      return this.i18n.t('/stockmatch/partialmatchtext', {
        matched: this.matchedCount,
        total: this.rows.length,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.stock-match-score {
  padding: var(--ds-space-3);
  border: 1px solid transparent;
  border-radius: var(--ds-radius-md);
  background: var(--ds-color-surface-sunken);

  p {
    margin: 0;
  }

  &--full {
    border-color: var(--ds-color-success);
  }
}

.stock-match-score__label {
  font-size: var(--ds-font-size-xs);
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ds-color-text-muted);
}

.stock-match-score__line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--ds-space-2);
}

.stock-match-score__value {
  font-size: var(--ds-font-size-lg);
  font-weight: 700;
  color: var(--ds-color-heading);

  .stock-match-score--full & {
    color: var(--ds-color-success);
  }
}

.stock-match-score__extras-badge {
  display: inline-flex;
  align-items: center;
  gap: var(--ds-space-1);
  padding: 1px var(--ds-space-2);
  border: 1px solid var(--ds-color-border);
  border-radius: var(--ds-radius-full);
  background: var(--ds-color-surface);
  font-size: var(--ds-font-size-xs);
  font-weight: 700;
  white-space: nowrap;
}

.stock-match-score__segments {
  display: flex;
  gap: 3px;
  margin: var(--ds-space-2) 0;

  span {
    flex: 1;
    height: 4px;
    border-radius: 2px;
    background: var(--ds-color-border);
  }

  .stock-match-score__segment--matched {
    background: var(--ds-color-success);
  }
}

.stock-match-score__muted {
  font-size: var(--ds-font-size-sm);
  color: var(--ds-color-text-muted);
}
</style>
